import {
  ArrowUpRightFromSquare,
  Avatar,
  Badge,
  Button,
  CakeCandlesIcon,
  ClipboardListIcon,
  cn,
  HandDotsIcon,
  Paragraph,
  PassportIcon,
  PhoneIcon,
  UserIcon,
} from '@quickmd-llc/design-system'
import moment from 'moment'
import { useCallback } from 'react'
import { getAge, formatPatientName } from 'utils/common'
import { IDVerificationBadge } from './StatusBadge'
import { DrugScreenModal } from './DrugScreenModal'
import { IDVerificationModal } from './IDVerificationModal'
import { type PatientStaffApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { usePatientCardActions } from './usePatientCardActions'
import { CopyableField } from './CopyableField'
import { formatPhoneNumber } from '@quickmd-llc/frontend-utils'

type Props = {
  patient: Partial<PatientStaffApiOutContract>
  avatarSize?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
  dataTypes?: string[]
  hasBadgeRow?: boolean
  includePreferredName?: boolean
  showChart?: boolean
  showPrep?: boolean
  onPrepVisitClick?: () => void
  hideSuccessBadges?: boolean
  isNewPatient?: boolean
}

export const PatientCard = ({
  patient,
  avatarSize = 'xs',
  dataTypes = [],
  hasBadgeRow,
  includePreferredName,
  showChart,
  showPrep,
  onPrepVisitClick,
  isNewPatient,
  hideSuccessBadges,
}: Props) => {
  const {
    cachedPatient,
    showVisitWave,
    isDrugScreenOpen,
    drugScreenView,
    isIdVerificationOpen,
    closeDrugScreen,
    openIdVerification,
    closeIdVerification,
    onOpenPatientChartPage,
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
  } = usePatientCardActions({ patientId: patient.PatientId, patient })

  const patientInfo = cachedPatient || patient
  const getPatientName = useCallback(
    () => formatPatientName(patientInfo, { includePreferred: includePreferredName }),
    [patientInfo, includePreferredName],
  )

  const getAvatar = useCallback(
    () => (
      <div className={cn('relative', (avatarSize === 'l' || avatarSize === 'xl' || avatarSize === 'xxl') && 'mr-sp-1')}>
        <Avatar
          name={`${patientInfo.FirstName} ${patientInfo.LastName}`}
          src={patientInfo.ImageUrl}
          size={avatarSize}
        />
        {showVisitWave ?
          <div
            className={cn(
              'absolute bottom-sp-0.25 right-sp-0.25 flex h-[11px] w-[11px] items-center justify-center rounded-full bg-white',
              {
                'bottom-sp-0.5 right-sp-0.5 flex h-[20px] w-[20px]':
                  avatarSize === 'l' || avatarSize === 'xl' || avatarSize === 'xxl',
              },
            )}>
            <div
              className={cn('h-sp-1 w-sp-1 rounded-full bg-seafoam-50', {
                'h-sp-2 w-sp-2': avatarSize === 'l' || avatarSize === 'xl' || avatarSize === 'xxl',
              })}
            />
          </div>
        : null}
      </div>
    ),
    [patientInfo, showVisitWave, avatarSize],
  )
  function getUserData(type: string) {
    switch (type) {
      case 'demo':
        return (
          <div
            className={cn('flex !flex-row items-center gap-sp-0.25 overflow-hidden', {
              'flex-col gap-sp-0.25': hasBadgeRow,
            })}>
            <UserIcon iconSize='s' color='text-text-dark' />
            <Paragraph size='sm' className='text-text-light'>
              {getAge(patientInfo.BirthDate)}/{patientInfo.Gender?.charAt(0)?.toLocaleUpperCase() ?? ''},{' '}
              {patientInfo.Address?.StateCode}
            </Paragraph>
          </div>
        )
      case 'mrn': {
        const mrn = patientInfo.MedicalRecordNumber
        if (!mrn) return null
        return (
          <CopyableField value={mrn} copyLabel='Copy MRN' ariaLabel={`Copy MRN ${mrn}`}>
            <PassportIcon iconSize='s' className='group-hover/copy:text-highlight' />
            <Paragraph className='text-text-light group-hover/copy:text-highlight' size='sm'>
              {mrn}
            </Paragraph>
          </CopyableField>
        )
      }
      case 'dob':
        return (
          <div className='flex items-center gap-sp-0.25'>
            <CakeCandlesIcon iconSize='s' color='text-text-dark' />
            <Paragraph className='text-text-light' size='sm'>
              {moment(patientInfo.BirthDate).format('MM/DD/YYYY')}
            </Paragraph>
          </div>
        )
      case 'phone': {
        const phone = formatPhoneNumber(patientInfo.PhoneNumber?.Number)
        if (!phone) return null
        return (
          <CopyableField value={phone} copyLabel='Copy number' ariaLabel={`Copy phone number ${phone}`}>
            <PhoneIcon iconSize='s' className='group-hover/copy:text-highlight' />
            <Paragraph className='text-text-light group-hover/copy:text-highlight' size='sm'>
              {phone}
            </Paragraph>
          </CopyableField>
        )
      }
      case 'allergies': {
        const allergies = patientInfo.AllergyCollection?.Allergies ?? []
        let allergyText: string | null = null
        if (allergies.length > 0) {
          // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
          allergyText = allergies.map((allergy) => `${allergy.Name} (${allergy.Severity})`).join(', ')
        } else if (patientInfo.AllergyCollection?.HasNoAllergyConfirmation) {
          allergyText = 'No known allergies'
        }
        if (!allergyText) return null
        return (
          <div className='flex items-center gap-sp-0.25'>
            <HandDotsIcon iconSize='s' color='text-text-dark' />
            <Paragraph className='text-text-light' size='sm'>
              {allergyText}
            </Paragraph>
          </div>
        )
      }
      default:
        return null
    }
  }
  return (
    <div
      {...(showChart ?
        {
          role: 'button',
          tabIndex: 0,
          onClick: onOpenPatientChartPage,
          onKeyDown: (e: React.KeyboardEvent) => {
            // Only act on the row itself — ignore Enter/Space bubbling up from child
            // controls (e.g. the Prep button) so they don't also open the chart.
            if (e.target !== e.currentTarget) return
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onOpenPatientChartPage()
            }
          },
        }
      : {})}
      className={cn(
        'flex w-full items-center gap-sp-1',
        showChart && 'cursor-pointer px-sp-2 py-sp-1 transition-colors hover:bg-coastal-blue-10',
      )}>
      {getAvatar()}
      <div className={cn('flex flex-1 flex-col gap-sp-0.5')}>
        <div className='flex w-full flex-wrap items-center gap-sp-1 overflow-hidden'>
          <Paragraph className='font-semibold text-text-dark'>{getPatientName()}</Paragraph>
          {isNewPatient ?
            <Badge size='s' variant='active'>
              New patient
            </Badge>
          : null}
          <span role='presentation' onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            {/*
             // @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered */}
            <IDVerificationBadge
              // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
              verification={cachedPatient.IdentityVerification}
              onClick={openIdVerification}
              hideSuccess={hideSuccessBadges}
            />
          </span>
        </div>
        <div className='flex flex-wrap items-center gap-x-sp-1 gap-y-0.5 py-sp-0.5'>
          {dataTypes.map((type) => (
            <div key={type}>{getUserData(type)}</div>
          ))}
        </div>
      </div>
      {showChart ?
        <ArrowUpRightFromSquare size='sm' className='text-text-dark' aria-hidden />
      : null}
      {showPrep ?
        <Button
          priority='secondary'
          isIcon
          // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
          onClick={(e) => {
            e.stopPropagation()
            onPrepVisitClick?.()
          }}
          size='s'>
          <ClipboardListIcon /> Prep
        </Button>
      : null}

      {/*
        Both modals take the patient as a required prop, and `cachedPatient` falls back to
        the loosely-typed `patient` input, so it can be undefined. Gating the render on it
        is also the honest behaviour: neither modal has anything to show without a patient.
      */}
      {isDrugScreenOpen && cachedPatient ?
        <DrugScreenModal
          isOpen={isDrugScreenOpen}
          onClose={closeDrugScreen}
          view={drugScreenView}
          drugScreen={cachedPatient.DrugScreen}
          patient={cachedPatient}
        />
      : null}
      {isIdVerificationOpen && cachedPatient ?
        <IDVerificationModal patient={cachedPatient} isOpen={isIdVerificationOpen} onClose={closeIdVerification} />
      : null}
    </div>
  )
}

export default PatientCard
